import { useState } from "react";

import { IStudy } from "../../../@types/admin/AdminStudy";
import {
  useAddStudy,
  useUpdateStudy,
} from "../../../hooks/mutations/admin/studies";
import {
  useAllWardOptions,
  useConditionTypeOptions,
} from "../../../hooks/queries/admin/selectValues";
import { useToast } from "../../../hooks/useToast";
import { errorToastMessage } from "../../../utils/HttpError";
import {
  AdminDateField,
  AdminSelectField,
  CheckboxField,
  FieldError,
  FieldHint,
  FilterButton,
  FilterGroup,
  FilterInput,
  FilterLabel,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
} from "../shared";
import { durationInDaysFrom, studyFormDefaults } from "./studyFields";

/** `AddStudyRequestDto` caps both at 255 characters. */
const MAX_TEXT_LENGTH = 255;

interface IStudyModalProps {
  /** Omitted for a create. */
  study?: IStudy;
  onClose: () => void;
}

/**
 * Create/edit study — Retool's `modalFrame1` + `formStudy` (EN-5155), which
 * used one form for both modes, keyed off whether a table row was selected.
 *
 * The expiry date is the field an operator thinks in; the API takes
 * `durationInDays`. Retool converted with `diff(now)` on create and
 * `diff(created)` on edit — the same rule measured from the row's start date,
 * which `durationInDaysFrom` now expresses once.
 */
export function StudyModal({ study, onClose }: IStudyModalProps) {
  const isEdit = Boolean(study);
  const { showToast } = useToast();
  const { wards, isLoading: wardsLoading } = useAllWardOptions();
  const { conditionTypes, isLoading: conditionTypesLoading } =
    useConditionTypeOptions();
  const { addStudy, isAddingStudy } = useAddStudy();
  const { updateStudy, isUpdatingStudy } = useUpdateStudy();

  const defaults = studyFormDefaults(study);
  const [name, setName] = useState(defaults.name);
  const [code, setCode] = useState(defaults.code);
  const [expireAt, setExpireAt] = useState(defaults.expireAt);
  const [hospitalId, setHospitalId] = useState(defaults.hospitalId);
  const [conditionTypeId, setConditionTypeId] = useState(
    defaults.conditionTypeId
  );
  const [publish, setPublish] = useState(defaults.publish);
  // An empty create form is invalid by definition; a field only complains
  // once touched. An edit form is seeded from a real row, so it can validate
  // straight away — there is something to be wrong about.
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const isSaving = isAddingStudy || isUpdatingStudy;

  const touch = (field: string) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const durationInDays = expireAt
    ? durationInDaysFrom(study?.created, expireAt)
    : undefined;

  const errors = {
    name: name.trim() ? undefined : "Name is required.",
    code: code.trim() ? undefined : "Code is required.",
    expireAt: (() => {
      if (!expireAt) return "Expiry date is required.";
      // A negative duration is what the API would store, and the study would
      // be born expired — Retool sent it without comment.
      if (durationInDays !== undefined && durationInDays < 0) {
        return isEdit
          ? "Expiry must be on or after the study's created date."
          : "Expiry must be today or later.";
      }
      return undefined;
    })(),
    hospitalId: hospitalId ? undefined : "Select a ward.",
    conditionTypeId: conditionTypeId ? undefined : "Select a condition type.",
  };
  const hasErrors = Object.values(errors).some(Boolean);
  const shownError = (field: keyof typeof errors) =>
    isEdit || touched[field] ? errors[field] : undefined;

  const handleSave = async () => {
    if (hasErrors) return;

    try {
      if (study) {
        await updateStudy({
          id: Number(study.id),
          name: name.trim(),
          code: code.trim(),
          hospitalId: Number(hospitalId),
          conditionTypeId: Number(conditionTypeId),
          durationInDays,
          // The study's measurement types are managed on the ward, not here,
          // so the key is omitted: the API only applies a non-null array, and
          // sending [] (as Retool did) wiped the stored types on every edit.
          publish,
        });
        showToast("The study was updated successfully.", "success");
      } else {
        await addStudy({
          name: name.trim(),
          code: code.trim(),
          hospitalId: Number(hospitalId),
          conditionTypeId: Number(conditionTypeId),
          durationInDays,
          measurementTypes: [],
          publish,
        });
        showToast("The study was created successfully.", "success");
      }
      onClose();
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          isEdit
            ? "There was an error updating this study."
            : "There was an error creating this study."
        ),
        "warning"
      );
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>
        {isEdit ? `Edit study ${study?.id}` : "New study"}
      </ModalTitle>

      <ModalForm>
        <FilterGroup>
          <FilterLabel htmlFor="study-name">Name</FilterLabel>
          <FilterInput
            id="study-name"
            type="text"
            maxLength={MAX_TEXT_LENGTH}
            value={name}
            onBlur={() => touch("name")}
            onChange={(e) => {
              touch("name");
              setName(e.target.value);
            }}
          />
          {shownError("name") && <FieldError>{shownError("name")}</FieldError>}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="study-code">Signup code</FilterLabel>
          <FilterInput
            id="study-code"
            type="text"
            maxLength={MAX_TEXT_LENGTH}
            value={code}
            onBlur={() => touch("code")}
            onChange={(e) => {
              touch("code");
              setCode(e.target.value);
            }}
          />
          <FieldHint>
            Patients enter this code to join the study. It must be unique.
          </FieldHint>
          {shownError("code") && <FieldError>{shownError("code")}</FieldError>}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="study-expires">Expires at</FilterLabel>
          <AdminDateField
            id="study-expires"
            value={expireAt}
            onChange={(next) => {
              touch("expireAt");
              setExpireAt(next);
            }}
          />
          {durationInDays !== undefined && durationInDays >= 0 && (
            <FieldHint>
              {durationInDays === 1
                ? "Duration: 1 day"
                : `Duration: ${durationInDays} days`}
              {isEdit ? " from the created date." : " from today."}
            </FieldHint>
          )}
          {shownError("expireAt") && (
            <FieldError>{shownError("expireAt")}</FieldError>
          )}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="study-ward">Ward</FilterLabel>
          <AdminSelectField
            id="study-ward"
            value={hospitalId}
            disabled={wardsLoading}
            onBlur={() => touch("hospitalId")}
            onChange={(e) => {
              touch("hospitalId");
              setHospitalId(e.target.value);
            }}
          >
            <option value="">
              {wardsLoading ? "Loading..." : "Select an option"}
            </option>
            {wards.map((ward) => (
              <option key={ward.id} value={ward.id}>
                {ward.id} - {ward.name}
              </option>
            ))}
          </AdminSelectField>
          {shownError("hospitalId") && (
            <FieldError>{shownError("hospitalId")}</FieldError>
          )}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="study-condition">Condition type</FilterLabel>
          <AdminSelectField
            id="study-condition"
            value={conditionTypeId}
            disabled={conditionTypesLoading}
            onBlur={() => touch("conditionTypeId")}
            onChange={(e) => {
              touch("conditionTypeId");
              setConditionTypeId(e.target.value);
            }}
          >
            <option value="">
              {conditionTypesLoading ? "Loading..." : "Select an option"}
            </option>
            {conditionTypes.map((conditionType) => (
              <option key={conditionType.id} value={conditionType.id}>
                {conditionType.name}
              </option>
            ))}
          </AdminSelectField>
          {shownError("conditionTypeId") && (
            <FieldError>{shownError("conditionTypeId")}</FieldError>
          )}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Visibility</FilterLabel>
          <CheckboxField>
            <input
              type="checkbox"
              checked={publish}
              onChange={(e) => setPublish(e.target.checked)}
            />
            Published
          </CheckboxField>
        </FilterGroup>

        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isSaving}
            onClick={onClose}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isSaving || hasErrors}
            onClick={handleSave}
          >
            {(() => {
              if (isSaving) return isEdit ? "Saving..." : "Creating...";
              return isEdit ? "Save" : "Create";
            })()}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
